import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  Chip,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import { DelegationTask } from "protogen/delegations_pb";
import DateDisplay from "components/common/DateDisplay";
import { SecretRevealConfirmation } from "components/secrets/SecretRevealConfirmation";
import { AllDetails } from "./DelegationDetails";
import { getLightningTaskTypeDefinition } from "types/delegation";

const CHANGEABLE_STATUSES = [
  "pending",
  "queued",
  "in_progress",
  "delegated",
  "completed",
  "cancelled",
];

export const getStatusLabel = (status: string): string => {
  switch (status) {
    case "pending":
      return "Pending";
    case "queued":
      return "Queued";
    case "in_progress":
      return "In Progress";
    case "delegated":
      return "Delegated";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
};

export const getStatusColor = (
  status: string,
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning" => {
  switch (status) {
    case "pending":
      return "default";
    case "queued":
      return "warning";
    case "in_progress":
    case "delegated":
      return "primary";
    case "completed":
      return "success";
    case "cancelled":
      return "error";
    default:
      return "default";
  }
};

const formatDate = (sec: bigint | undefined): string => {
  if (!sec || sec === BigInt(0)) return "-";
  return new Date(Number(sec) * 1000).toLocaleString();
};

type Props = {
  task: DelegationTask;
  onStatusChange: (ref: string, newStatus: string) => void;
};

export default ({ task, onStatusChange }: Props) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [revealSecretRef, setRevealSecretRef] = useState<string | null>(null);
  const TaskTypeIcon = getLightningTaskTypeDefinition(task.serviceType).icon;

  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: "1px solid #ECECEC",
        width: "100%",
        background: open ? "#FBFBFB" : "white",
      }}
    >
      {/* Header - always visible */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          cursor: !open ? "pointer" : undefined,
        }}
        onClick={() => !open && setOpen(true)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <TaskTypeIcon size={16} />
          <Box>
            <Typography variant="bodyHeavy">{task.title}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Typography variant="bodySmall" color="text.secondary">
                {task.creator?.displayName || "Unknown"}
              </Typography>
              {task.createdSec !== undefined &&
                task.createdSec !== BigInt(0) && (
                  <>
                    <Typography variant="bodySmall" color="text.secondary">
                      ·
                    </Typography>
                    <DateDisplay
                      date={new Date(Number(task.createdSec) * 1000)}
                    />
                  </>
                )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Chip
            label={getStatusLabel(task.status)}
            color={getStatusColor(task.status)}
            size="small"
          />
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/tasks/subtasks/${encodeURIComponent(task.ref)}`);
            }}
            sx={{ color: "text.secondary" }}
          >
            <ExternalLink size={16} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </IconButton>
        </Box>
      </Box>

      {/* Expanded content */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            padding: "16px 24px",
            borderTop: "1px solid #ECECEC",
            background: "white",
          }}
        >
          {/* Description */}
          {task.description && (
            <Box sx={{ marginBottom: "16px" }}>
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{ marginBottom: "4px" }}
              >
                Instructions
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {task.description}
              </Typography>
            </Box>
          )}

          <AllDetails
            detailsJson={task.detailsJson}
            variant="small"
            onRevealSecret={setRevealSecretRef}
          />

          {/* Timestamps */}
          <Box
            sx={{
              display: "flex",
              gap: "24px",
              flexWrap: "wrap",
              color: "text.secondary",
            }}
          >
            {task.delegatedSec !== undefined &&
              task.delegatedSec !== BigInt(0) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Clock size={14} />
                  <Typography variant="bodySmall">
                    Delegated: {formatDate(task.delegatedSec)}
                  </Typography>
                </Box>
              )}
            {task.completedSec !== undefined &&
              task.completedSec !== BigInt(0) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <CheckCircle2 size={14} />
                  <Typography variant="bodySmall">
                    Completed: {formatDate(task.completedSec)}
                  </Typography>
                </Box>
              )}
            {task.timeSpentMinutes > 0 && (
              <Typography variant="bodySmall">
                Time spent: {task.timeSpentMinutes}m
              </Typography>
            )}
          </Box>

          {/* Comments */}
          {task.comments && task.comments.length > 0 && (
            <Box
              sx={{
                marginTop: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{ marginBottom: "4px" }}
              >
                Comments
              </Typography>
              {task.comments.map((comment) => (
                <Box
                  key={comment.ref}
                  sx={{
                    padding: "8px",
                    backgroundColor: "#F5F5F5",
                    borderRadius: "4px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "4px",
                    }}
                  >
                    <Typography variant="bodySmallHeavy">
                      {comment.author?.displayName || "Unknown"}
                    </Typography>
                    {comment.updatedStatus && (
                      <Typography variant="bodySmall" color="text.secondary">
                        → {getStatusLabel(comment.updatedStatus)}
                      </Typography>
                    )}
                  </Box>
                  {comment.textContent && (
                    <Typography variant="bodySmall">
                      {comment.textContent}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Status change dropdown - at bottom */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "16px",
              paddingTop: "16px",
              borderTop: "1px solid #ECECEC",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setStatusMenuAnchor(e.currentTarget);
              }}
              endIcon={<ChevronDown size={14} />}
            >
              Change Status
            </Button>
            <Menu
              anchorEl={statusMenuAnchor}
              open={Boolean(statusMenuAnchor)}
              onClose={() => setStatusMenuAnchor(null)}
            >
              {CHANGEABLE_STATUSES.filter((s) => s !== task.status).map(
                (status) => (
                  <MenuItem
                    key={status}
                    onClick={() => {
                      onStatusChange(task.ref, status);
                      setStatusMenuAnchor(null);
                    }}
                  >
                    {getStatusLabel(status)}
                  </MenuItem>
                ),
              )}
            </Menu>
          </Box>
        </Box>
      </Collapse>

      {revealSecretRef && (
        <SecretRevealConfirmation
          open={!!revealSecretRef}
          onClose={() => setRevealSecretRef(null)}
          secretRef={revealSecretRef}
        />
      )}
    </Box>
  );
};
